import React from "react";
import {
  Box,
  Card,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";

type PrefKeys = "email" | "bookings" | "reminders";

type Props = {
  prefs: Record<PrefKeys, boolean>;
  onChange: (k: PrefKeys, v: boolean) => void;
};

const options: [PrefKeys, string][] = [
  ["email", "General Emails"],
  ["bookings", "Booking Requests"],
  ["reminders", "Session Reminders"],
];

/** --- Netflix-like palette tokens (local to component) --- */
const NF = {
  bg: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.22)",
  red: "#E50914",
  redHover: "#b20710",
  glow:
    "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.10), transparent 55%), radial-gradient(600px 300px at 110% 0%, rgba(255,255,255,0.05), transparent 60%)",
};

const NotificationSettings: React.FC<Props> = ({ prefs, onChange }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Card
      variant="outlined"
      sx={{
        fontFamily: "'Lexend', sans-serif",
        color: NF.text,
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${NF.border}`,
        background: `${NF.glow}, ${NF.bg}`,
        boxShadow: "0 24px 60px rgba(0,0,0,.55)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack spacing={0.75} sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Typography
          sx={{
            fontWeight: 900,
            letterSpacing: ".2px",
            textShadow: "0 1px 0 rgba(0,0,0,.35)",
            fontSize: { xs: "1.05rem", sm: "1.15rem" },
          }}
        >
          Notifications
        </Typography>
        <Typography
          sx={{ color: NF.muted, fontSize: { xs: ".9rem", sm: ".95rem" } }}
        >
          Choose what you want to be notified about.
        </Typography>
      </Stack>

      {/* Options */}
      <Stack
        spacing={0}
        divider={<Divider sx={{ borderColor: NF.border }} />}
        sx={{
          border: `1px solid ${NF.border}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {options.map(([k, label]) => {
          const checked = !!prefs[k];
          return (
            <Box
              key={k}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.25, sm: 1.5 },
                backgroundColor: "rgba(255,255,255,0.02)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: ".95rem", sm: "1rem" },
                }}
              >
                {label}
              </Typography>

              <Switch
                checked={checked}
                onChange={(_, c) => onChange(k, c)}
                inputProps={{ "aria-label": label }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: NF.red,
                    "&:hover": { backgroundColor: "rgba(229,9,20,0.08)" },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: NF.red,
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "rgba(255,255,255,0.28)",
                  },
                }}
              />
            </Box>
          );
        })}
      </Stack>

      {/* Helper note for very small screens */}
      <Typography
        sx={{
          mt: { xs: 1.25, sm: 1.5 },
          color: NF.muted,
          fontSize: { xs: ".84rem", sm: ".9rem" },
        }}
      >
        You can change these anytime.
      </Typography>
    </Card>
  );
};

export default NotificationSettings;
